import clsx from "clsx";
import { useFormik } from "formik";
import { ArrowLeft, ArrowRight, Eye, EyeOff, RotateCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import { getLoadCaptcha, login } from "../core/_requests";
import { useAuth } from "../core/Auth";

const loginSchema = Yup.object().shape({
	username: Yup.string().required("Username is required"),
	password: Yup.string().required("Password is required"),
	captcha: Yup.string().required("Captcha is required"),
});

const initialValues = {
	username: "",
	password: "",
	captcha: "",
};

export function Login() {
	const [loading, setLoading] = useState(false);
	const [captchaLoading, setCaptchaLoading] = useState(false);
	const [svg, setSvg] = useState("");
	const [sessionId, setSessionId] = useState("");
	const navigate = useNavigate();
	const { saveAuth, setCurrentUser } = useAuth();
	const [showPassword, setShowPassword] = useState(false);
	const [mounted, setMounted] = useState(true);

	const formik = useFormik({
		initialValues,
		validationSchema: loginSchema,
		onSubmit: async (values, { setSubmitting }) => {
			setLoading(true);
			try {
				// Real login behavior
				const { data: response } = await login(
					values.username,
					values.password,
					values.captcha,
					sessionId,
				);

				if (response.status === 1 && response.data) {
					const authData = {
						...response.data,
						access_token: response.data.token || "", // Map backend 'token' to frontend 'access_token'
					};
					saveAuth(authData);
					setCurrentUser(authData as any);
					localStorage.setItem("Logined", "true");
					toast.success("Logged in successfully");
					navigate("/dashboard");
				} else {
					throw new Error(response.message || "Login failed");
				}
			} catch (_error: any) {
				setSubmitting(false);
				setLoading(false);
				const errorMessage =
					_error.response?.data?.message || _error.message || "Login failed";
				formik.setStatus(errorMessage);
				loadCaptcha();
			}
		},
	});

	const loadCaptcha = useCallback(async () => {
		setCaptchaLoading(true);
		try {
			const response = await getLoadCaptcha();
			const captchaData = response?.data?.data || response?.data;

			if (captchaData?.svg) {
				let svgStr = captchaData.svg.trim();
				if (!svgStr.includes("viewBox")) {
					svgStr = svgStr.replace("<svg", '<svg viewBox="0 0 150 50"');
				}
				setSvg(svgStr);
				setSessionId(captchaData.sessionId || "");
				formik.setFieldValue("captcha", "");
			}
		} catch (error) {
			console.error("Failed to load CAPTCHA", error);
		} finally {
			setCaptchaLoading(false);
		}
	}, [formik.setFieldValue]);

	useEffect(() => {
		loadCaptcha(); // Initial load
		const interval = setInterval(() => {
			loadCaptcha();
		}, 120000); // 2 minutes refresh

		return () => clearInterval(interval);
	}, [loadCaptcha]);

	return (
		<div
			className="container-fluid min-vh-100 d-flex align-items-center justify-content-center p-0"
			// style={{ background: "#f8f9fa" }}
		>
			<div
				className="row w-100 shadow-lg rounded-4 overflow-hidden mx-auto bg-white"
				style={{ maxWidth: "1000px", minHeight: "600px" }}
			>
				{/* Left Side: Navy Blue */}
				<div
					className="col-lg-6 d-none d-lg-flex flex-column justify-content-center p-12 text-white position-relative"
					style={{
						background: "linear-gradient(135deg, #0d1642 0%, #1a237e 100%)",
					}}
				>
					{/* Logo Top Absolute */}
					<div
						className="position-absolute top-0 start-0 w-100 p-12 d-flex justify-content-center justify-content-md-start"
						style={{ zIndex: 2 }}
					>
						<div className="d-flex align-items-center gap-4">
							<img
								src={toAbsoluteUrl("media/logos/Tamil_Nadu_Emblem.png")}
								alt="TN Government Emblem"
								style={{
									height: "52px",
									width: "auto",
									backgroundColor: "white",
									borderRadius: "8px",
									padding: "3px 6px",
									objectFit: "contain",
									boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
								}}
							/>
							<div className="d-flex flex-column text-start" style={{ lineHeight: 1.2 }}>
								<span
									className="text-white fw-bolder"
									style={{
										fontSize: "19px",
										letterSpacing: "0.2px",
										fontFamily: "Georgia, 'Times New Roman', serif",
									}}
								>
									Social Audit Unit of Tamil Nadu
								</span>
								<span
									className="text-white-50 mt-1"
									style={{
										fontSize: "12px",
										fontWeight: 500,
									}}
								>
									Rural Development &amp; Panchayat Raj Department
								</span>
							</div>
						</div>
					</div>

					{/* Centered Text Content */}
					<div
						style={{ zIndex: 2 }}
						className="w-100 text-center text-md-start mt-10"
					>
						<div>
							<h1
								className="text-white lh-base mb-6 fw-bolder"
								style={{ fontSize: "2rem" }}
							>
								Ensuring Accountability in <br /> Public Welfare
							</h1>
							<h4
								className="fst-italic lh-base mb-6 text-white"
								style={{ fontSize: "1.2rem", fontWeight: "400", opacity: 0.9 }}
							>
								Empowering communities to monitor public programs with
								transparency, ensuring every resource is accounted for and every
								voice is heard.
							</h4>
							<p
								className="fw-bold text-uppercase ls-1 text-white-50"
								style={{ fontSize: "0.95rem" }}
							>
								Social Audit Unit of Tamil Nadu
							</p>
						</div>
					</div>

					{/* Links at the bottom center absolute */}
					<div
						className="position-absolute bottom-0 start-0 w-100 pb-8 d-flex justify-content-center gap-8"
						style={{ zIndex: 2 }}
					>
						<Link
							to="/terms"
							className="text-white-50 text-hover-white text-decoration-none fw-semibold fs-7 transition-all"
						>
							Terms & Conditions
						</Link>
						<Link
							to="/privacy"
							className="text-white-50 text-hover-white text-decoration-none fw-semibold fs-7 transition-all"
						>
							Privacy Policy
						</Link>
					</div>

					{/* Subtle dot pattern overlay */}
					<div
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundImage:
								"radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)",
							backgroundSize: "20px 20px",
							zIndex: 1,
						}}
					></div>
				</div>

				{/* Right Side: Form */}
				<div className="col-lg-6 bg-white p-12 d-flex flex-column justify-content-center align-items-center">
					<div className=" col-lg-10 p-5  mx-auto">
						<div className="mb-10 text-center">
							<h2 className="fw-bolder fs-1 mb-3 text-dark">Login</h2>
							{/* <p className="text-muted fw-semibold fs-6">
								Please enter your credentials to access the admin dashboard.
							</p> */}
						</div>

						<form className="form " onSubmit={formik.handleSubmit} noValidate>
							{formik.status && (
								<div className="alert alert-danger mb-5">{formik.status}</div>
							)}

							{/* Username */}
							<div className="mb-4">
								<input
									{...formik.getFieldProps("username")}
									type="text"
									placeholder="Username"
									autoComplete="off"
									className={clsx(
										"form-control form-control-lg bg-light rounded-3 border-1",
										{
											"is-invalid":
												formik.touched.username && formik.errors.username,
										},
									)}
									style={{ padding: "15px 20px" }}
								/>
								{formik.touched.username && formik.errors.username && (
									<div className="invalid-feedback">
										{formik.errors.username}
									</div>
								)}
							</div>

							{/* Password */}
							<div className="mb-4 position-relative">
								<input
									{...formik.getFieldProps("password")}
									placeholder="Password"
									type={showPassword ? "text" : "password"}
									autoComplete="off"
									className={clsx(
										"form-control form-control-lg bg-light rounded-3 border-1",
										{
											"is-invalid":
												formik.touched.password && formik.errors.password,
										},
									)}
									style={{ padding: "15px 20px" }}
								/>
								<button
									type="button"
									className="btn btn-icon btn-active-color-primary position-absolute end-0 top-50 translate-middle-y me-3"
									onClick={() => setShowPassword(!showPassword)}
									style={{ border: "none", background: "transparent" }}
								>
									{showPassword ? (
										<EyeOff className="text-muted" size={20} />
									) : (
										<Eye className="text-muted" size={20} />
									)}
								</button>
								{formik.touched.password && formik.errors.password && (
									<div className="invalid-feedback">
										{formik.errors.password}
									</div>
								)}
							</div>

							{/* Captcha */}
							<div className="mb-5">
								<div className="d-flex align-items-center gap-3 mb-3">
									<div className="flex-grow-1">
										<input
											{...formik.getFieldProps("captcha")}
											type="text"
											placeholder="Captcha"
											maxLength={6}
											autoComplete="off"
											className={clsx(
												"form-control form-control-lg bg-light rounded-3 border-1",
												{
													"is-invalid":
														formik.touched.captcha && formik.errors.captcha,
												},
											)}
											style={{ padding: "15px 20px" }}
										/>
									</div>
									<div
										className="bg-light rounded-3 d-flex align-items-center justify-content-center overflow-hidden captcha-svg-wrapper"
										style={{ minWidth: "150px", height: "54px" }}
									>
										{captchaLoading ? (
											<span className="spinner-border spinner-border-sm text-primary"></span>
										) : (
											<div
												dangerouslySetInnerHTML={{ __html: svg }}
												className="captcha-svg-container w-100 h-100 d-flex align-items-center justify-content-center"
											/>
										)}
									</div>
									<button
										type="button"
										className="btn btn-icon btn-light-primary rounded-3"
										onClick={() => loadCaptcha()}
										disabled={captchaLoading}
										style={{ height: "54px", width: "54px" }}
									>
										<RotateCw
											className={clsx(captchaLoading && "animate-spin")}
											size={20}
										/>
									</button>
								</div>
								{formik.touched.captcha && formik.errors.captcha && (
									<div className="text-danger fs-7">
										{formik.errors.captcha}
									</div>
								)}
							</div>

							<div className="d-flex justify-content-end	 flex-stack mb-10">
								{/* <div className="form-check form-check-custom form-check-solid">
									<input
										className="form-check-input"
										type="checkbox"
										name="remember"
									/>
									<label className="form-check-label fw-semibold text-gray-700 fs-6 ms-2">
										Remember me
									</label>
								</div> */}
								<Link
									to="/forgot-password"
									className="link-primary fw-bold text-decoration-none"
								>
									Forgot Password?
								</Link>
							</div>

							<div className="d-grid mb-10">
								<button
									type="submit"
									className="btn btn-lg text-white rounded-3 d-flex align-items-center justify-content-center gap-2"
									style={{
										background:
											"linear-gradient(135deg, #0d1642 0%, #1a237e 100%)",
										padding: "15px",
									}}
									disabled={loading}
								>
									{!loading ? (
										<>
											<span className="indicator-label">Sign In</span>
											<ArrowRight className="ms-2" size={20} />
										</>
									) : (
										<span className="spinner-border spinner-border-sm"></span>
									)}
								</button>
							</div>

							{/* <div className="text-center">
								<a
									href="https://tnsocialaudit.com"
									target="_blank"
									rel="noreferrer"
									className="text-gray-500 fw-semibold fs-6 text-decoration-none d-flex align-items-center justify-content-center gap-2 hover-primary"
								>
									<ArrowLeft size={18} />
									Back to Website
								</a>
							</div> */}
						</form>
					</div>
				</div>
			</div>
		</div>
	);
}
